import { EllipsisIcon } from "lucide-react";
import { createPortal } from "react-dom";
import { Link, NavLink } from "react-router";

import { useMe } from "@/api/hooks/auth";

import { courseAction, PEOPLE_NAV, PRIMARY_NAV, type NavItem } from "./nav-config";

interface TabBarProps {
  /** Opens the full navigation (the sidebar sheet). */
  onMore: () => void;
}

/**
 * The phone tab bar, the same in every Hume app (components/assets/css/pwa.css): home, the role's main
 * screens, the main action raised in the middle, then More for the whole menu. Hidden from lg up, where the
 * sidebar shows everything. Rendered into <body>, where the stylesheet expects it.
 */
export function TabBar({ onMore }: TabBarProps) {
  const me = useMe();
  if (!me) return null;
  const items: NavItem[] = me.role === "instructor" ? [...PRIMARY_NAV, PEOPLE_NAV] : [...PRIMARY_NAV];
  const action = me.role !== "instructor" || me.can_create_courses ? courseAction(me.role) : null;
  // The action sits after the first two entries, as in Hume Hire and Hume Manage.
  const before = items.slice(0, 2);
  const after = items.slice(2, 3);

  return createPortal(
    <nav className="tabbar" aria-label="Tab bar">
      {before.map((item) => (
        <Tab key={item.to} item={item} />
      ))}
      {action && (
        <span className="tabbar-action">
          <Link to={action.to} aria-label={action.label} title={action.label}>
            <action.icon aria-hidden />
          </Link>
        </span>
      )}
      {after.map((item) => (
        <Tab key={item.to} item={item} />
      ))}
      <button type="button" className="tabbar-item" onClick={onMore}>
        <EllipsisIcon aria-hidden />
        <span>More</span>
      </button>
    </nav>,
    document.body,
  );
}

function Tab({ item }: { item: NavItem }) {
  return (
    <NavLink to={item.to} end={item.end} className="tabbar-item">
      <item.icon aria-hidden />
      <span>{item.label}</span>
    </NavLink>
  );
}
